import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AgreementForm, registerPatient } from '../api/bookingApi';
import LoadingState from './LoadingState';
import BirthdayField from './custom-form/BirthdayField';
import { parseBirthday, type BirthdayParts } from './custom-form/birthday';
import { getLocalToday } from '../utils/date';

interface NewPatientStepProps {
  clientId: string;
  phone: string;
  agreementForms?: AgreementForm[];
  acceptedInsurances?: string[];
  onRegistered: (r: { patientToken: string; patientName: string; dob?: string }) => void;
  onBack: () => void;
}

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  address_line1: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  insurance_provider: string;
  insurance_member_id: string;
  consent_name: string;
  consent_agreed: boolean;
}

export default function NewPatientStep({ clientId, phone, acceptedInsurances, onRegistered, onBack }: NewPatientStepProps) {
  const [form, setForm] = useState<FormState>({
    first_name: '',
    last_name: '',
    email: '',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    insurance_provider: '',
    insurance_member_id: '',
    consent_name: '',
    consent_agreed: false,
  });
  const [parts, setParts] = useState<BirthdayParts>({ month: '', day: '', year: '' });
  const [dobError, setDobError] = useState('');
  const [insuranceCarrier, setInsuranceCarrier] = useState('');
  const [insuranceOther, setInsuranceOther] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const birthday = parseBirthday(parts, getLocalToday());
    if (birthday.status !== 'valid') {
      const messages = {
        empty: '"Date of birth" is required.',
        partial: 'Enter the month, day and year for "Date of birth".',
        invalid: '"Date of birth" must be a real date.',
        future: `"Date of birth" can't be in the future.`,
      };
      setDobError(messages[birthday.status]);
      return;
    }
    setDobError('');
    const carrier = acceptedInsurances?.length
      ? (insuranceCarrier === 'Other' ? insuranceOther : insuranceCarrier)
      : insuranceCarrier;
    if (!carrier.trim()) {
      setError('Please enter your insurance carrier.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        date_of_birth: birthday.iso,
        phone,
        email: form.email || undefined,
        insurance_carrier: carrier,
      };
      const result = await registerPatient(clientId, payload);
      if (result.success === false) { setError(result.message ?? 'Registration failed.'); return; }
      onRegistered({ patientToken: result.patient_token, patientName: `${form.first_name} ${form.last_name}`, dob: birthday.iso });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingState key="loading" type="registration" message="Creating Account" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="label">First name</label>
                <input className="input-field" required value={form.first_name}
                  onChange={(e) => set('first_name', e.target.value)} placeholder="Jane" />
              </div>
              <div>
                <label className="label">Last name</label>
                <input className="input-field" required value={form.last_name}
                  onChange={(e) => set('last_name', e.target.value)} placeholder="Smith" />
              </div>
            </div>

            {/* DOB + Email */}
            <div>
              <BirthdayField
                field={{ label: 'Date of birth', required: false }}
                inputId="register-dob"
                parts={parts}
                onPartChange={(part, value) => {
                  setParts((current) => ({ ...current, [part]: value }));
                  setDobError('');
                }}
                errorId={dobError ? 'register-dob-error' : undefined}
                className="input-field text-base"
              />
              {dobError && <p id="register-dob-error" role="alert" className="error-text">{dobError}</p>}
            </div>

            <div>
              <label className="label">Email address</label>
              <input type="email" className="input-field" required value={form.email}
                onChange={(e) => set('email', e.target.value)} placeholder="jane@example.com" />
            </div>

            <div>
              <label className="label">Insurance carrier <span className="text-red-500">*</span></label>
              {acceptedInsurances?.length ? (
                <>
                  <select className="input-field" value={insuranceCarrier}
                    onChange={(e) => setInsuranceCarrier(e.target.value)}>
                    <option value="">Select insurance…</option>
                    {acceptedInsurances.map((ins) => (
                      <option key={ins} value={ins}>{ins}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {insuranceCarrier === 'Other' && (
                    <input type="text" className="input-field mt-2"
                      placeholder="Enter your insurance carrier"
                      value={insuranceOther}
                      onChange={(e) => setInsuranceOther(e.target.value)} />
                  )}
                </>
              ) : (
                <input type="text" className="input-field"
                  placeholder="e.g. Delta Dental"
                  value={insuranceCarrier}
                  onChange={(e) => setInsuranceCarrier(e.target.value)} />
              )}
            </div>

            {error && <p className="error-text">{error}</p>}

            <button type="submit" className="btn-primary" disabled={loading}>
              Continue
            </button>

            <button type="button" className="btn-secondary" onClick={onBack}>
              Use a different phone number
            </button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
